import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const USER_TYPES = [
  { id: "user", label: "General user", desc: "Browse everything, book appointments, order and enrol." },
  { id: "employer", label: "Employer", desc: "Post jobs and hire from the community." },
  { id: "seller", label: "Seller", desc: "Open a storefront and list items in the marketplace." },
  { id: "teacher", label: "Teacher / Faculty", desc: "Contribute training videos and mentor learners." },
  { id: "organizer", label: "Event organizer", desc: "Post community events and manage RSVPs." },
  { id: "doctor", label: "Doctor", desc: "Answer consults and manage clinic appointments." },
  { id: "lawyer", label: "Lawyer", desc: "Answer legal queries and manage client consultations." },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("user");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(name, email, password, userType);
      toast.success("Welcome to Anveshan!");
      // Redirect to the right onboarding page based on user_type
      const redirectMap = { doctor: "/doctors", lawyer: "/lawyers", employer: "/jobs", seller: "/marketplace", teacher: "/training", organizer: "/events" };
      nav(redirectMap[userType] || "/");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md samaaj-card p-8" data-testid="register-card">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Get started</div>
        <h1 className="font-heading text-4xl mt-2 text-[#1C1B1A]">Join Anveshan</h1>
        <p className="text-sm text-[#5C564D] mt-2">Pick how you&apos;ll use the community. You can change this anytime from your profile.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#5C564D]">I&apos;m registering as</label>
            <select
              data-testid="register-user-type"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[#E5D9CC] bg-[#F9F6F0] focus:outline-none focus:border-[#D95A41]"
            >
              {USER_TYPES.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
            </select>
            <div className="text-[11px] text-[#5C564D] mt-1">
              {USER_TYPES.find((t) => t.id === userType)?.desc}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C564D]">Full name</label>
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[#E5D9CC] bg-[#F9F6F0] focus:outline-none focus:border-[#D95A41]"
              placeholder="Your name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C564D]">Email</label>
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[#E5D9CC] bg-[#F9F6F0] focus:outline-none focus:border-[#D95A41]"
              placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C564D]">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[#E5D9CC] bg-[#F9F6F0] focus:outline-none focus:border-[#D95A41]"
              placeholder="At least 6 characters" />
          </div>
          {err && <div className="text-sm text-red-700" data-testid="register-error">{err}</div>}
          <button data-testid="register-submit" disabled={busy} className="samaaj-btn-primary w-full">
            {busy ? "Creating…" : "Create my account"}
          </button>
        </form>
        <div className="mt-6 text-sm text-center text-[#5C564D]">
          Already a member? <Link to="/login" data-testid="link-login" className="text-[#D95A41] font-semibold link-underline">Log in</Link>
        </div>
      </div>
    </div>
  );
}
